/**
 * Aggregations and statistics — Dashboard and Annual report.
 * Pure functions over Surgery[]; no side effects.
 * MONTHS_* constants stay for the English ficha engine; UI labels should use
 * the localized monthShort/monthLong from ./dates instead.
 */
import type { Surgery, YearFilter, Role, Technique } from './types';
import { monthShort } from './dates';

export const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export function yearOf(c: Surgery): number {
  return new Date(c.fecha).getFullYear();
}

export function monthOf(c: Surgery): number {
  return new Date(c.fecha).getMonth(); // 0-11
}

export function filterByYear(surgeries: Surgery[], filter: YearFilter): Surgery[] {
  if (filter === 'all') return surgeries;
  return surgeries.filter((c) => yearOf(c) === filter);
}

/** Inclusive ISO date bounds (`YYYY-MM-DD`). */
export function filterByDateRange(
  surgeries: Surgery[],
  from: string | null | undefined,
  to: string | null | undefined,
): Surgery[] {
  let r = surgeries;
  if (from) r = r.filter((c) => c.fecha >= from);
  if (to) r = r.filter((c) => c.fecha <= to);
  return r;
}

/** Years with activity, descending */
export function availableYears(surgeries: Surgery[]): number[] {
  return [...new Set(surgeries.map(yearOf))].sort((a, b) => b - a);
}

export function sortByDateDesc(surgeries: Surgery[]): Surgery[] {
  return [...surgeries].sort((a, b) => b.fecha.localeCompare(a.fecha));
}

/** Median of a numeric array; returns undefined for empty arrays. */
export function median(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

export function latest(surgeries: Surgery[], n = 5): Surgery[] {
  return sortByDateDesc(surgeries).slice(0, n);
}

/* ────────────────────────── volume ────────────────────────── */

export interface MonthPoint {
  month: number; // 0-11
  label: string;
  total: number;
  minutes: number;
}

/** 12 points (Jan–Dec) of the given year; months without data get total 0 */
export function volumeByMonth(surgeries: Surgery[], year: number): MonthPoint[] {
  const points: MonthPoint[] = Array.from({ length: 12 }, (_, month) => ({
    month, label: monthShort(month), total: 0, minutes: 0,
  }));
  for (const c of surgeries) {
    if (yearOf(c) !== year) continue;
    const p = points[monthOf(c)];
    p.total += 1;
    p.minutes += c.duracionMin;
  }
  return points;
}

export interface YearPoint {
  year: number;
  label: string;
  total: number;
  minutes: number;
}

export function volumeByYear(surgeries: Surgery[]): YearPoint[] {
  const map = new Map<number, YearPoint>();
  for (const c of surgeries) {
    const y = yearOf(c);
    const p = map.get(y) ?? { year: y, label: String(y), total: 0, minutes: 0 };
    p.total += 1;
    p.minutes += c.duracionMin;
    map.set(y, p);
  }
  return [...map.values()].sort((a, b) => a.year - b.year);
}

/** Top procedures without collapsing the tail into “Other”. */
export function byProcedure(surgeries: Surgery[], max = 12): CategoryCount[] {
  const map = new Map<string, number>();
  for (const c of surgeries) map.set(c.categoria, (map.get(c.categoria) ?? 0) + 1);
  return [...map.entries()]
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
    .slice(0, max);
}

/* ────────────────────────── distributions ────────────────────────── */

export interface CategoryCount {
  name: string;
  total: number;
}

/** Top N categories + "Other" */
export function byCategory(surgeries: Surgery[], max = 6): CategoryCount[] {
  const map = new Map<string, number>();
  for (const c of surgeries) map.set(c.categoria, (map.get(c.categoria) ?? 0) + 1);
  const sorted = [...map.entries()]
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  if (sorted.length <= max) return sorted;
  const top = sorted.slice(0, max);
  const rest = sorted.slice(max).reduce((s, x) => s + x.total, 0);
  return [...top, { name: 'Other', total: rest }];
}

export interface TechniqueCount {
  technique: Technique;
  total: number;
}

export function byTechnique(surgeries: Surgery[]): TechniqueCount[] {
  const map = new Map<Technique, number>();
  for (const c of surgeries) map.set(c.tecnica, (map.get(c.tecnica) ?? 0) + 1);
  return [...map.entries()]
    .map(([technique, total]) => ({ technique, total }))
    .sort((a, b) => b.total - a.total);
}

export interface RoleCount {
  role: Role;
  total: number;
  pct: number; // 0-100
}

const ROLE_ORDER: Role[] = [
  'Lead surgeon',
  'First assistant',
  'Second assistant',
  'Supervisor',
];

export function byRole(surgeries: Surgery[]): RoleCount[] {
  const total = surgeries.length;
  return ROLE_ORDER.map((role) => {
    const n = surgeries.filter((c) => c.rol === role).length;
    return { role, total: n, pct: total > 0 ? (n / total) * 100 : 0 };
  }).filter((r) => r.total > 0);
}

/* ────────────────────────── complications and duration ────────────────────────── */

export interface ComplicationStats {
  total: number;
  withComplications: number;
  rate: number; // percent 0-100
  byGrade: { grade: string; total: number }[];
}

export function complicationStats(surgeries: Surgery[]): ComplicationStats {
  const withComp = surgeries.filter((c) => c.complicaciones.hubo);
  const grades = new Map<string, number>();
  for (const c of withComp) {
    const g = c.complicaciones.grado ?? 'n/a';
    grades.set(g, (grades.get(g) ?? 0) + 1);
  }
  return {
    total: surgeries.length,
    withComplications: withComp.length,
    rate: surgeries.length > 0 ? (withComp.length / surgeries.length) * 100 : 0,
    byGrade: [...grades.entries()]
      .map(([grade, total]) => ({ grade, total }))
      .sort((a, b) => a.grade.localeCompare(b.grade)),
  };
}

export interface DurationStats {
  mean: number;
  min: number;
  max: number;
  totalMin: number;
}

export function durationStats(surgeries: Surgery[]): DurationStats {
  if (surgeries.length === 0) return { mean: 0, min: 0, max: 0, totalMin: 0 };
  const durations = surgeries.map((c) => c.duracionMin);
  const totalMin = durations.reduce((s, d) => s + d, 0);
  return {
    mean: Math.round(totalMin / surgeries.length),
    min: Math.min(...durations),
    max: Math.max(...durations),
    totalMin,
  };
}

export function hoursInOr(surgeries: Surgery[]): number {
  return Math.round((durationStats(surgeries).totalMin / 60) * 10) / 10;
}

/* ────────────────────────── new techniques and streak ────────────────────────── */

/** Techniques used for the first time in the given year (not seen before) */
export function newTechniques(surgeries: Surgery[], year: number): Technique[] {
  const previous = new Set<Technique>(
    surgeries.filter((c) => yearOf(c) < year).map((c) => c.tecnica),
  );
  const thisYear = new Set<Technique>(
    surgeries.filter((c) => yearOf(c) === year).map((c) => c.tecnica),
  );
  return [...thisYear].filter((t) => !previous.has(t));
}

/** Consecutive months with activity ending at the most recent month with data */
export function monthStreak(surgeries: Surgery[]): number {
  if (surgeries.length === 0) return 0;
  const keys = new Set(surgeries.map((c) => `${yearOf(c)}-${monthOf(c)}`));
  const last = sortByDateDesc(surgeries)[0];
  let year = yearOf(last);
  let month = monthOf(last);
  let streak = 0;
  while (keys.has(`${year}-${month}`)) {
    streak += 1;
    month -= 1;
    if (month < 0) { month = 11; year -= 1; }
  }
  return streak;
}

/* ────────────────────────── lessons ────────────────────────── */

export interface RecentLesson {
  id: string;
  folio: string;
  categoria: string; // category name (wire contract)
  text: string;
  fecha: string; // ISO date (wire contract)
  fromFicha: boolean;
}

/** Latest lessons: structured-summary ones first, then the free-text field */
export function recentLessons(surgeries: Surgery[], n = 3): RecentLesson[] {
  const result: RecentLesson[] = [];
  for (const c of sortByDateDesc(surgeries)) {
    if (c.ficha && c.ficha.lecciones.length > 0) {
      result.push({
        id: c.id, folio: c.folio, categoria: c.categoria,
        text: c.ficha.lecciones[0], fecha: c.fecha, fromFicha: true,
      });
    } else if (c.lecciones.trim().length > 0) {
      result.push({
        id: c.id, folio: c.folio, categoria: c.categoria,
        text: c.lecciones.trim(), fecha: c.fecha, fromFicha: false,
      });
    }
    if (result.length >= n) break;
  }
  return result;
}

/* ────────────────────────── dashboard KPIs ────────────────────────── */

export interface YearKpis {
  procedures: number;
  deltaProceduresPct: number | null; // % vs previous year (null: no baseline)
  hours: number;
  deltaHours: number | null; // hours vs previous year
  complicationRate: number; // %
  deltaRatePt: number | null; // percentage points vs previous year
  newTechniques: Technique[];
  byMonth: MonthPoint[];
}

export function yearKpis(surgeries: Surgery[], year: number): YearKpis {
  const current = filterByYear(surgeries, year);
  const previous = filterByYear(surgeries, year - 1);
  const hours = hoursInOr(current);
  const hoursPrev = hoursInOr(previous);
  const rate = complicationStats(current).rate;
  const ratePrev = complicationStats(previous).rate;
  return {
    procedures: current.length,
    deltaProceduresPct:
      previous.length > 0 ? ((current.length - previous.length) / previous.length) * 100 : null,
    hours,
    deltaHours: previous.length > 0 ? Math.round((hours - hoursPrev) * 10) / 10 : null,
    complicationRate: rate,
    deltaRatePt: previous.length > 0 ? Math.round((rate - ratePrev) * 10) / 10 : null,
    newTechniques: newTechniques(surgeries, year),
    byMonth: volumeByMonth(surgeries, year),
  };
}

/* ────────────────────────── annual report ────────────────────────── */

export interface AnnualSummary {
  year: number;
  total: number;
  hours: number;
  complicationRate: number;
  byMonth: MonthPoint[];
  byCategory: CategoryCount[];
  byTechnique: TechniqueCount[];
  byRole: RoleCount[];
  duration: DurationStats;
  newTechniques: Technique[];
  withComplications: Surgery[];
  lessons: RecentLesson[];
  comparison: {
    previousYear: number;
    previousTotal: number;
    deltaTotalPct: number | null;
    previousHours: number;
    deltaHours: number | null;
    previousRate: number;
    deltaRatePt: number | null;
  };
}

export function annualSummary(surgeries: Surgery[], year: number): AnnualSummary {
  const current = filterByYear(surgeries, year);
  const previous = filterByYear(surgeries, year - 1);
  const kpis = yearKpis(surgeries, year);
  return {
    year,
    total: current.length,
    hours: kpis.hours,
    complicationRate: kpis.complicationRate,
    byMonth: kpis.byMonth,
    byCategory: byCategory(current),
    byTechnique: byTechnique(current),
    byRole: byRole(current),
    duration: durationStats(current),
    newTechniques: kpis.newTechniques,
    withComplications: sortByDateDesc(current.filter((c) => c.complicaciones.hubo)),
    lessons: recentLessons(current, 24),
    comparison: {
      previousYear: year - 1,
      previousTotal: previous.length,
      deltaTotalPct: kpis.deltaProceduresPct,
      previousHours: hoursInOr(previous),
      deltaHours: kpis.deltaHours,
      previousRate: complicationStats(previous).rate,
      deltaRatePt: kpis.deltaRatePt,
    },
  };
}

